import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import PasswordSchema, { PasswordParams } from "users/auth/credentials/passwords/password-schema";
import { ZodError } from "zod";

export default class Password {
  public static create(values: PasswordParams): Password {
    return new Password(values.password, values.confirmPassword);
  }

  public static fromJSON(json: unknown): Password | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = PasswordSchema.validateJSON(json);
    return new Password(parsed);
  }

  public get value(): string {
    return this.password;
  }

  public constructor(public readonly password: string, confirmPassword?: string) {
    try {
      PasswordSchema.validate({ password, confirmPassword }, false);
      this.password = password;
    } catch (error) {
      if (error instanceof Error && error.cause instanceof ZodError && Array.isArray(error.cause.issues)) {
        throw InvalidPasswordError.fromIssues(error.cause.issues);
      }
    }
  }

  public toJSON(): string {
    return this.password;
  }

  public isEqualTo(other: Password): boolean {
    return this.value === other?.value;
  }

  public toString(): string {
    return this.password;
  }
}

export class InvalidPasswordError extends Error {
  public static fromIssues(issues: readonly { path: PropertyKey[]; message: string }[]): InvalidPasswordError {
    const confirms = (issue: { path: PropertyKey[] }) => issue.path[0] === "confirmPassword";

    return new InvalidPasswordError(
      issues.filter((issue) => !confirms(issue)).map((issue) => issue.message),
      issues.filter(confirms).map((issue) => issue.message),
    );
  }

  /** What is wrong with the password itself. */
  public readonly issues: string[];
  /** What is wrong with the confirmation of the password, which is a separate field. */
  public readonly confirmationIssues: string[];

  constructor(issues: string[], confirmationIssues: string[] = []) {
    super(`Invalid password: ${[...issues, ...confirmationIssues].join(", ")}`);
    this.issues = issues;
    this.confirmationIssues = confirmationIssues;
  }
}
